<script lang="ts">
	//Hooks
	import { onMount } from 'svelte';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { parseSchedules, prepareToSave } from 'src/helpers/actions/schedules/core/schedule_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';
	import { capFirstChar } from 'src/helpers/utilities';
	import { isProThreeEM } from 'src/helpers/interface_helpers';
	import { getScheduleList } from 'src/helpers/get_schedules_gen_2';
	//Services
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Design system components
	import { Alert, Button, Loader, Text, Toggle } from '@Shelly/shelly-design-system';
	//Components
	import SchedulesList from './SchedulesList.svelte';
	import CreateSchedulesG2 from './CreateSchedulesG2.svelte';
	//Types
	import type {
		SchedulesG2Request,
		ScheduleParams,
		ScheduleRequestData,
	} from '../../../../types/requests/schedules/schedules_g2';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';

	export let device: Device;

	const doNotHaveScheduleSetting =
		/^(sensor|power_meter|inputs_reader|thermostat|gateway)$/.test(device.category) ||
		(/^(module)$/.test(device.category) && /^(shell|inputs_reader)$/.test(device.mode)) ||
		isProThreeEM(device.type);

	const getSchedules = async () => {
		waitForResponse = true;
		schedules = [];
		const res: SchedulesG2Request = await getScheduleList(device);

		if (res?.isok) {
			const jobs = res?.data?.jobs || res?.jobs || [];
			Object.values(jobs).forEach((schedule) => {
				const params = parseSchedules(schedule);
				if (params && params.calls.find((i) => /^Shelly.Update$/i.test(i?.method))) {
					schedules.push(params);
				}
			});
		}
		autoUpdateUnavailable = !res?.isok;
		waitForResponse = false;
	};

	const editSchedule = async (data: ScheduleParams) => {
		showWizard = true;
		selectedSchedule = data;
	};

	const toggleAction = async (data: ScheduleParams) => {
		const res: SettingRequest = await gen2Request(device, 'schedule.update', {
			id: data.id,
			enable: data.enable,
		});
		onSettingsResponse(res, device, {
			success: data.enable
				? $format('schedule_enabled_scc_toast', {
						default: 'Schedule enabled.',
					})
				: $format('schedule_disabled_scc_toast', {
						default: 'Schedule disabled.',
					}),
		});
	};

	const createSchedule = async () => {
		waitForCreateResponse = true;
		const schedule: ScheduleRequestData = {
			enable: true,
			timespec: '0 0 0 * * *',
			calls: [
				{
					method: 'Shelly.Update',
					params: { stage: 'stable' },
					origin: 'shelly_service',
				},
			],
		};
		const data: ScheduleParams = parseSchedules(schedule);
		const res: SettingRequest = await gen2Request(device, 'schedule.create', prepareToSave(data));
		if (res?.isok) getSchedules();
		else schedules = [];
		waitForCreateResponse = false;
		onSettingsResponse(res, device, {
			success: $format('sched_save_scc_toast', {
				default: 'Schedule saved successfully.',
			}),
		});
	};

	const deleteAction = async (data: ScheduleParams) => {
		waitForCreateResponse = true;
		const res: SettingRequest = await gen2Request(device, 'schedule.delete', {
			id: data.id,
		});
		if (res?.isok) getSchedules();
		else schedules = schedules;
		waitForCreateResponse = false;
		onSettingsResponse(res, device, {
			success: $format('action_deleted_scc_toast', {
				default: 'Action was deleted.',
			}),
		});
	};

	const onToggle = () => {
		schedules.length > 0 ? deleteAction(schedules[0]) : createSchedule();
	};

	onMount(getSchedules);

	let schedules: ScheduleParams[] = [],
		selectedSchedule: ScheduleParams = null,
		showWizard = false,
		autoUpdateUnavailable = false,
		waitForCreateResponse = false,
		waitForResponse = false;
</script>

{#if waitForResponse}
	<div class="d-flex justify-content-center">
		<Loader class="mb-m" />
	</div>
{:else if autoUpdateUnavailable}
	<Alert
		type="warning"
		class="mb-s"
		isHeadingBold
		heading={$format('firmware_auto_update_unavailable_heading', {
			default: 'Automatic update settings unavailable',
		})}
		message={$format('firmware_auto_update_unavailable_message', {
			default: "Your device's automatic firmware update settings are currently unavailable.",
		})}
	/>
	<Button
		size="m"
		width="full"
		variant="primary"
		class="mb-s"
		on:click={getSchedules}
		aria-label={$format('button_retry', { default: 'Retry' })}
	>
		{capFirstChar($format('button_retry', { default: 'Retry' }))}
	</Button>
	<hr class="mb-m mt-m" />
{:else if doNotHaveScheduleSetting && schedules.length}
	<SchedulesList
		list={schedules}
		deviceId={device.id}
		on:edit={(e) => editSchedule(e.detail)}
		on:delete={(e) => deleteAction(e.detail)}
		on:toggle={(e) => {
			toggleAction(e.detail);
		}}
	/>
	<div class="d-flex mb-s" />
{:else}
	<Text styleType="body-small" class="text-bold mb-s ml-s">
		{$format('schedule_firmware_update_automatically', {
			default: 'Update automatically',
		})}
	</Text>
	<Text styleType="body-small" class="mb-s ml-s">
		{$format('schedule_firmware_update_to_latest_stable', {
			default: 'Adds a schedule to check and update to latest stable firmware version',
		})}
	</Text>
	<Toggle
		size="m"
		class="mb-xs ml-s"
		checked={schedules.length > 0}
		disabled={waitForCreateResponse}
		on:click={onToggle}
		id="toggle-7gjsh44"
	>
		{$format('schedule_firmware_update_enable_auto_update', {
			default: 'Enable auto update',
		})}
	</Toggle>
	{#if schedules.length > 0}
		<Text
			class="mb-s ml-s link-styles text-small"
			on:click={() => navigateToSetting(device.id, 'schedule', '')}
		>
			{capFirstChar(
				$format('button_edit_schedule', {
					default: 'edit schedule',
				}),
			)}
		</Text>
	{/if}
	<hr class="mb-m mt-m" />
{/if}
{#if showWizard}
	<CreateSchedulesG2 {device} schedule={selectedSchedule} bind:showWizard on:save={getSchedules} />
{/if}
